/**
 * Client Themes — Barrel Export
 *
 * Registry of all supported brand themes.
 * To add a new theme:
 *   1. Create `<name>.theme.ts` with exact Figma hex values
 *   2. Add the id to ClientThemeId in types.ts
 *   3. Register it in clientThemeRegistry below
 */
export type { ClientThemeId, ThemeMode, BrandTokens } from './types';
export { blueTheme } from './blue.theme';
export { resolveClientOverrides, applyClientOverride } from './themeResolver';
export type { ClientThemeOverrides } from './themeResolver';

import type { ClientThemeId, BrandTokens } from './types';
import { blueTheme } from './blue.theme';
import { palette } from '../colorGroups/palette.colors';

/**
 * Central registry mapping theme ids → brand token objects.
 * Add new client themes here once Figma values are confirmed.
 */
export const clientThemeRegistry: Record<ClientThemeId, BrandTokens> = {
  blue: blueTheme,

  // ── Placeholder entries — replace with real Figma values in <name>.theme.ts ──
  orange: {
    primary: '#CC5500',
    primaryHover: '#A84400',
    primaryPressed: '#7A3200',
    primaryTint: '#FFF3E8',
    primaryTintMed: '#FFDDB5',
    primaryBorder: '#FFA855',
    primaryBadge: '#A84400',
    onPrimary: palette.gray.white,           // '#FFFFFF'
    onTint: palette.blue.onTint,             // '#020617'
    pageBackground: palette.backgrounds.orangeAppBG, // '#FFF8F2'
    /** Soft orange — midpoint of primaryTintMed (#FFDDB5) ↔ primaryBorder (#FFA855) */
    primarySoft: '#FFC385',
  },
  teal: {
    primary: '#0F766E',
    primaryHover: '#0D5C56',
    primaryPressed: '#093D39',
    primaryTint: '#E6FAF8',
    primaryTintMed: '#B2E8E4',
    primaryBorder: '#5ECAC3',
    primaryBadge: '#0D5C56',
    onPrimary: palette.gray.white,           // '#FFFFFF'
    onTint: palette.blue.onTint,             // '#020617'
    pageBackground: palette.backgrounds.tealAppBG, // '#F0FAF9'
    /** Soft teal — midpoint of primaryTintMed (#B2E8E4) ↔ primaryBorder (#5ECAC3) */
    primarySoft: '#88D9D4',
  },
  purple: {
    primary: '#6A1B9A',
    primaryHover: '#4A148C',
    primaryPressed: '#12005E',
    primaryTint: '#F3E5F5',
    primaryTintMed: '#E1BEE7',
    primaryBorder: '#CE93D8',
    primaryBadge: '#4A148C',
    onPrimary: palette.gray.white,           // '#FFFFFF'
    onTint: palette.blue.onTint,             // '#020617'
    pageBackground: palette.backgrounds.purpleAppBG, // '#F9F5FF'
    /** Soft purple — midpoint of primaryTintMed (#E1BEE7) ↔ primaryBorder (#CE93D8) */
    primarySoft: '#D7A9DF',
  },
  slate: {
    primary: '#475569',
    primaryHover: '#334155',
    primaryPressed: '#1E293B',
    primaryTint: '#F1F5F9',
    primaryTintMed: '#E2E8F0',
    primaryBorder: '#CBD5E1',
    primaryBadge: '#334155',
    onPrimary: palette.gray.white,           // '#FFFFFF'
    onTint: palette.blue.onTint,             // '#020617'
    pageBackground: palette.backgrounds.slateAppBG, // '#F8FAFC'
    /** Soft slate — midpoint of primaryTintMed (#E2E8F0) ↔ primaryBorder (#CBD5E1) */
    primarySoft: '#D7DFE9',
  },
};

/**
 * Look up brand tokens for a given theme id.
 * Falls back to blue if the id is not registered.
 */
export function getClientThemeTokens(id: ClientThemeId): BrandTokens {
  return clientThemeRegistry[id] ?? clientThemeRegistry.blue;
}